// useMarketWraps.js
// Custom hook to fetch daily and weekly market roundups from Firestore.
// Mirrors the pattern used in useMarketData.js
import { useState, useEffect } from 'react';
import { getMarketWrapService } from '../Services/MarketWrapService';

/**
 * Hook to load market wraps from Firestore.
 *
 * @param {'daily'|'weekly'|'both'} [wrapType='both']
 * @param {number} [limitCount=10]
 * @returns {{ dailyWraps, weeklyWraps, latestDaily, latestWeekly, loading, error, refetch }}
 */
const useMarketWraps = (wrapType = 'both', limitCount = 10) => {
    const [dailyWraps, setDailyWraps] = useState([]);
    const [weeklyWraps, setWeeklyWraps] = useState([]);
    const [latestDaily, setLatestDaily] = useState(null);
    const [latestWeekly, setLatestWeekly] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const service = getMarketWrapService();

    const fetchData = async () => {
        setLoading(true);
        setError(null);
        try {
            const fetches = [];

            if (wrapType === 'daily' || wrapType === 'both') {
                fetches.push(
                    service.getWraps('daily', limitCount).then((data) => {
                        setDailyWraps(data);
                        setLatestDaily(data[0] || null);
                    })
                );
            }

            if (wrapType === 'weekly' || wrapType === 'both') {
                fetches.push(
                    service.getWraps('weekly', limitCount).then((data) => {
                        setWeeklyWraps(data);
                        setLatestWeekly(data[0] || null);
                    })
                );
            }

            await Promise.all(fetches);
        } catch (err) {
            console.error('[useMarketWraps] Fetch error:', err);
            setError(err.message || 'Failed to load market roundups');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wrapType, limitCount]);

    return {
        dailyWraps,
        weeklyWraps,
        latestDaily,
        latestWeekly,
        loading,
        error,
        refetch: fetchData,
    };
};

export default useMarketWraps;
